import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useAuth } from '../context/AuthContext';
import { useFilters } from '../context/FilterContext';
import { dashboardAPI } from '../services/api';

const FilterBar = ({ onApplyFilters }) => {
  const { user } = useAuth();
  const isFacilityUser = !!user?.facility_name;
  const { filters, updateFilter, resetFilters } = useFilters();
  
  const [facilities, setFacilities] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  const years = ['2022', '2023', '2024', '2025', '2026'];
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  useEffect(() => {
    const loadFilterData = async () => {
      if (isFacilityUser) return;
      
      try {
        setLoading(true);
        
        const [facilitiesRes, regionsRes, typesRes] = await Promise.all([
          dashboardAPI.getAllFacilities(),
          dashboardAPI.getRegionsList(),
          dashboardAPI.getFacilityTypes(),
        ]);

        setFacilities(facilitiesRes.data?.all_facilities || facilitiesRes.all_facilities || []);
        setFacilityTypes(facilitiesRes.data?.facility_types || facilitiesRes.facility_types || typesRes.data?.facility_types || typesRes.facility_types || []);
        setRegions(regionsRes.data?.regions || regionsRes.regions || ['Hhohho', 'Lubombo', 'Manzini', 'Shiselweni']);

      } catch (error) {
        console.error('Error loading filter data:', error);
        setRegions(['Hhohho', 'Lubombo', 'Manzini', 'Shiselweni']);
      } finally {
        setLoading(false);
      }
    };

    loadFilterData();
  }, [isFacilityUser]);

  useEffect(() => {
    let filtered = facilities;

    if (filters.region) {
      filtered = filtered.filter(f => f.nurses_region === filters.region);
    }

    if (filters.facility_type) {
      filtered = filtered.filter(f => f.facility_type === filters.facility_type);
    }

    setFilteredFacilities(filtered);
  }, [filters.region, filters.facility_type, facilities]);

  const handleChange = (field, value) => {
    if ((field === 'region' || field === 'facility_type') && filters.facility_name) {
      updateFilter('facility_name', '');
    }
    updateFilter(field, value);
  };

  // ✅ NEW: Export functionality
  const handleExport = async () => {
    try {
      setExporting(true);
      
      const exportParams = {
        region: filters.region || undefined,
        facility_name: filters.facility_name || (isFacilityUser ? user?.facility_name : undefined),
        facility_type: filters.facility_type || undefined,
        year: filters.year || undefined,
        month_start: filters.month_from || undefined,
        month_end: filters.month_to || undefined,
        gender: filters.gender || undefined,
        form_type: filters.form_type || undefined,
      };

      // Remove undefined values
      const cleanParams = Object.fromEntries(
        Object.entries(exportParams).filter(([_, v]) => v !== undefined && v !== '')
      );

      const response = await dashboardAPI.exportFilteredData(cleanParams);
      
      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename with date and filters
      const date = new Date().toISOString().split('T')[0];
      const facilityPart = cleanParams.facility_name ? `_${cleanParams.facility_name.replace(/\s+/g, '_')}` : '';
      const yearPart = cleanParams.year ? `_${cleanParams.year}` : '';
      a.download = `EDFS_Data${facilityPart}${yearPart}_${date}.xlsx`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Data exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Export failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.year) count++;
    if (filters.month_from && filters.month_from !== 1) count++;
    if (filters.month_to && filters.month_to !== 12) count++;
    if (filters.gender) count++;
    if (filters.form_type) count++;
    if (!isFacilityUser) {
      if (filters.region) count++;
      if (filters.facility_name) count++;
      if (filters.facility_type) count++;
    }
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
            {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
            {activeFilterCount > 0 && (
              <Chip label={activeFilterCount} color="primary" size="small" />
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Region - Only for admin users */}
          {!isFacilityUser && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  value={filters.region || ''}
                  label="Region"
                  onChange={(e) => handleChange('region', e.target.value)}
                  MenuProps={menuProps}
                  disabled={loading}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Facility Type - Only for admin users */}
          {!isFacilityUser && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Facility Type</InputLabel>
                <Select
                  value={filters.facility_type || ''}
                  label="Facility Type"
                  onChange={(e) => handleChange('facility_type', e.target.value)}
                  MenuProps={menuProps}
                  disabled={loading}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {facilityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Facility Name - Only for admin users */}
          {!isFacilityUser && (
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Facility</InputLabel>
                <Select
                  value={filters.facility_name || ''}
                  label="Facility"
                  onChange={(e) => handleChange('facility_name', e.target.value)}
                  MenuProps={menuProps}
                  disabled={loading}
                >
                  <MenuItem value="">All Facilities</MenuItem>
                  {filteredFacilities.map((facility, idx) => (
                    <MenuItem key={`${facility.facility_name}-${idx}`} value={facility.facility_name}>
                      {facility.facility_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Year */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.year || ''}
                label="Year"
                onChange={(e) => handleChange('year', e.target.value)}
                MenuProps={menuProps}
              >
                <MenuItem value="">All Years</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Month From */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select
                value={filters.month_from || 1}
                label="Month From"
                onChange={(e) => handleChange('month_from', e.target.value)}
                MenuProps={menuProps}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Month To */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select
                value={filters.month_to || 12}
                label="Month To"
                onChange={(e) => handleChange('month_to', e.target.value)}
                MenuProps={menuProps}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Gender */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={filters.gender || ''}
                label="Gender"
                onChange={(e) => handleChange('gender', e.target.value)}
                MenuProps={menuProps}
              >
                <MenuItem value="">All Genders</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Form Type */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Form Type</InputLabel>
              <Select
                value={filters.form_type || ''}
                label="Form Type"
                onChange={(e) => handleChange('form_type', e.target.value)}
                MenuProps={menuProps}
              >
                <MenuItem value="">All Forms</MenuItem>
                <MenuItem value="1">Foot Forms</MenuItem>
                <MenuItem value="2">Eye Forms</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              {/* ✅ NEW: Export Button */}
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                onClick={handleExport}
                disabled={exporting}
                sx={{
                  backgroundColor: '#10b981',
                  '&:hover': { backgroundColor: '#059669' },
                  '&:disabled': { backgroundColor: '#9ca3af' }
                }}
              >
                {exporting ? 'Exporting...' : 'Export Data'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => {
                  resetFilters();
                  if (onApplyFilters) onApplyFilters();
                }}
                disabled={activeFilterCount === 0}
              >
                Reset ({activeFilterCount})
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ✅ Snackbar for export feedback */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FilterBar;